<template>
  <div
    class="flex gap-4 p-4 text-sm text-primary"
    :class="{ 'opacity-60': isSoldOut }"
  >
    <div class="flex flex-1">
      <NuxtLink
        :to="pdpUrl"
        class="flex-none shrink-0"
        :class="{
          'w-2/5 pr-6': isAddToBasketPopover && isMobile,
          'w-1/5 pr-6': isAddToBasketPopover && !isMobile,
          'w-auto': !isAddToBasketPopover,
        }"
        @click="$emit('close')"
      >
        <div
          :class="{
            'w-16': !isAddToBasketPopover,
            'w-full': isAddToBasketPopover,
          }"
        >
          <NuxtImg
            v-if="imageHash"
            :src="imageHash"
            :alt="title"
            class="object-cover"
            height="144"
          />
        </div>
      </NuxtLink>
      <div
        class="ml-2 flex grow flex-col"
        :class="{
          'flex-row': isLightVariant,
          'justify-center': !isAddToBasketPopover,
        }"
      >
        <NuxtLink
          :to="pdpUrl"
          class="text-sm font-bold"
          @click="$emit('close')"
        >
          <span :class="{ 'text-base': isAddToBasketPopover }">{{
            title
          }}</span>
        </NuxtLink>
        <div :class="{ 'text-base': isAddToBasketPopover }">
          <div>
            {{ $t('basket_card.size_label') }}:
            <span class="font-bold">{{ size }}</span>
          </div>
          <div
            v-if="variant && variant.attributes && variant.attributes.cupsize"
          >
            {{ $t('basket_card.cup_size_label') }}:
            {{ cupsizeLabel }}
          </div>
          <div>
            {{ $t('basket_card.quantity_label') }}:
            <span class="font-bold">{{ quantity }}</span>
          </div>
          <div>
            {{ $t('basket_card.color_label') }}:
            <span class="font-bold capitalize">{{ color }}</span>
          </div>
        </div>
        <div v-if="isAddToBasketPopover" class="flex items-start">
          <section class="flex flex-col items-end justify-end">
            <p
              v-if="showPriceReduction && reducedPrice"
              class="text-gray-500 text-base line-through"
            >
              {{ toCurrency(price + reducedPrice) }}
            </p>
            <Headline
              tag="p"
              size="base"
              :class="{ 'text-red': reducedPrice && showPriceReduction }"
            >
              {{ toCurrency(price) }}
            </Headline>
          </section>
        </div>
        <div class="mt-auto">
          <slot />
        </div>
      </div>
    </div>
    <div v-if="!isAddToBasketPopover" class="flex items-start">
      <p v-if="isSoldOut" class="whitespace-nowrap text-base font-bold">
        {{ $t('global.sold_out') }}
      </p>
      <section v-else class="flex flex-col items-end justify-end">
        <p
          v-if="showPriceReduction && reducedPrice"
          class="text-gray-500 text-base line-through"
        >
          {{ toCurrency(price + reducedPrice) }}
        </p>
        <Headline
          tag="p"
          size="base"
          :class="{ 'text-red': reducedPrice && showPriceReduction }"
        >
          {{ toCurrency(price) }}
        </Headline>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  getFirstAttributeValue,
  type Product,
  type Variant,
} from '@scayle/storefront-nuxt'
import { getProductDetailRoute } from '~/utils/route'
import { toCurrency } from '#layers/base/utils/price'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  variant: {
    type: Object as PropType<Variant>,
    required: true,
  },
  title: {
    type: String as PropType<string>,
    required: true,
  },
  articleNo: {
    type: String as PropType<string>,
    required: true,
  },
  color: {
    type: String as PropType<string>,
    required: true,
  },
  price: {
    type: Number as PropType<number>,
    required: true,
  },
  reducedPrice: {
    type: Number as PropType<number>,
    default: undefined,
  },
  imageHash: {
    type: String as PropType<string>,
    default: undefined,
  },
  quantity: {
    type: Number as PropType<number>,
    default: 1,
  },
  availableQuantity: {
    type: Number as PropType<number>,
    default: undefined,
  },
  size: {
    type: String as PropType<string>,
    default: '',
  },
  isOnWishlist: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  isSoldOut: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  hasBasketAction: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  hasWishlistAction: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  hasQuantityAction: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  isLightVariant: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  showPriceReduction: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  isAddToBasketPopover: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  orderNumber: {
    type: String as PropType<string>,
    default: undefined,
  },
})

const { toLocalePath } = useRouteHelpers()

defineEmits([
  'close',
  'click:delete',
  'click:add-to-wishlist',
  'click:remove-from-wishlist',
])

const { isMobile } = useDeviceType()
const cupsizeLabel = computed(
  () =>
    getFirstAttributeValue(props.variant?.attributes, 'cupsize')?.label || '',
)

const pdpUrl = computed(() => {
  if (!props.orderNumber) {
    return toLocalePath(getProductDetailRoute(props.product))
  } else {
    return `${toLocalePath(getProductDetailRoute(props.product))}?ordernumber=${props.orderNumber?.replace('-', '')}`
  }
})
</script>
